import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, update, set } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  MagnifyingGlassIcon, XMarkIcon, PencilIcon,
  UserIcon, ShieldCheckIcon, CalendarIcon,
  WalletIcon, CreditCardIcon, ClockIcon,
  ExclamationTriangleIcon, CheckCircleIcon,
  NoSymbolIcon, ArrowPathIcon, FunnelIcon,
  ChevronDownIcon, EyeIcon, CameraIcon,
  SparklesIcon, BoltIcon, QrCodeIcon,
  ArrowDownTrayIcon, PhoneIcon, EnvelopeIcon,
  IdentificationIcon, KeyIcon, UserGroupIcon,
  CheckIcon, DocumentTextIcon,
} from '@heroicons/react/24/outline';

// ─── SWAL Z ───────────────────────────────────────────────────────────────────
const SWAL_Z = { customClass: { container: 'um-swal-z' } };
if (typeof document !== 'undefined' && !document.getElementById('um-swal-z-style')) {
  const s = document.createElement('style');
  s.id = 'um-swal-z-style';
  s.textContent = '.um-swal-z{z-index:99999!important}';
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, ...SWAL_Z });

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const PRIMARY = '#800020';
const DARK    = '#1a1a2e';

const MEMBERSHIP_STATUSES = ['active', 'expired', 'pending', 'inactive'];
const ROLES = ['user', 'admin', 'superadmin'];

const PLAN_KEYS = [
  { key: 'weekly',    label: 'Weekly',    days: 7   },
  { key: 'monthly',   label: 'Monthly',   days: 30  },
  { key: 'quarterly', label: 'Quarterly', days: 120 },
  { key: 'biannual',  label: 'Bi-Annual', days: 180 },
  { key: 'yearly',    label: 'Yearly',    days: 365 },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmtDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtDateTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fmtDuration = (mins) => {
  if (!mins) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + 'm' : ''}`.trim() : `${m}m`;
};
const daysLeft = (expiryTs) => {
  if (!expiryTs) return null;
  const diff = expiryTs - Date.now();
  return Math.ceil(diff / 86400000);
};

const getMembershipBadge = (status, expiryTs) => {
  const days = daysLeft(expiryTs);
  if (status === 'active') {
    if (days !== null && days <= 7) return { label: `Expires in ${days}d`, color: '#d97706', bg: '#fffbeb' };
    return { label: 'Active', color: '#16a34a', bg: '#f0fdf4' };
  }
  if (status === 'expired') return { label: 'Expired',  color: '#dc2626', bg: '#fef2f2' };
  if (status === 'pending') return { label: 'Pending',  color: '#2563eb', bg: '#eff6ff' };
  return                           { label: 'Inactive', color: '#6b7280', bg: '#f9fafb' };
};

const getRoleBadge = (role) => {
  if (role === 'superadmin') return { label: 'Super Admin', color: '#fff', bg: DARK };
  if (role === 'admin')      return { label: 'Admin',       color: PRIMARY, bg: '#fef2f2' };
  return                            { label: 'Member',      color: '#6b7280', bg: '#f3f4f6' };
};

// ─── PDF EXPORT ───────────────────────────────────────────────────────────────
const exportMembersPDF = (users) => {
  const dateStr = new Date().toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' });
  const rows = users.map(u => {
    const mb = getMembershipBadge(u.membership?.status, u.membership?.expiryDate);
    return `
      <tr>
        <td>${u.personalInfo?.name || '—'}</td>
        <td>${u.personalInfo?.email || '—'}</td>
        <td>${u.personalInfo?.phone || '—'}</td>
        <td>${u.personalInfo?.role || '—'}</td>
        <td style="color:${mb.color};font-weight:600">${mb.label}</td>
        <td>${fmtDate(u.membership?.expiryDate)}</td>
        <td>KES ${(u.wallet?.balance || 0).toLocaleString()}</td>
        <td>${u.personalInfo?.facePhotoUrl ? '✅ Set' : '❌ Not set'}</td>
        <td>${u.blacklisted ? '🚫 Yes' : '—'}</td>
      </tr>`;
  }).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Member Report</title>
  <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11px;color:#1a1a2e;padding:24px}
  .title{font-size:20px;font-weight:800;color:#800020;margin-bottom:4px}.subtitle{font-size:11px;color:#6b7280;margin-bottom:20px}
  table{width:100%;border-collapse:collapse;font-size:10.5px}
  thead tr{background:#1a1a2e;color:#fff}th{padding:8px 6px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.04em}
  tbody tr:nth-child(even){background:#f8f9fa}td{padding:6px;border-bottom:1px solid #f1f5f9}
  .footer{margin-top:16px;font-size:10px;color:#9ca3af;text-align:right}
  @media print{body{padding:10mm}}</style></head>
  <body>
  <div class="title">👥 Member Report</div>
  <div class="subtitle">Generated on ${dateStr} · ${users.length} members</div>
  <table><thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Membership</th><th>Expiry</th><th>Wallet</th><th>Face Scan</th><th>Blacklisted</th></tr></thead>
  <tbody>${rows}</tbody></table>
  <div class="footer">User Management · ${dateStr}</div>
  <script>window.onload=()=>window.print()</script></body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const UserManagement = () => {
  const { userData, currentUser } = useAuth();
  const userRole     = (userData?.personalInfo?.role || '').toLowerCase();
  const isAuthorized = userRole === 'admin' || userRole === 'superadmin';
  const isSuperAdmin = userRole === 'superadmin';

  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole,   setFilterRole]   = useState('all');
  const [filterFace,   setFilterFace]   = useState('all');
  const [filterBlacklist, setFilterBlacklist] = useState('all');
  const [sortBy,       setSortBy]       = useState('name');
  const [sortDir,      setSortDir]      = useState('asc');
  const [showFilters,  setShowFilters]  = useState(false);

  // modals
  const [viewModal,    setViewModal]    = useState(null);
  const [editModal,    setEditModal]    = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await get(ref(database, 'users'));
      if (snap.exists()) {
        const list = Object.entries(snap.val()).map(([id, u]) => ({ id, ...u }));
        setUsers(list);
      } else {
        setUsers([]);
      }
    } catch (e) {
      console.error(e);
      toast('error', 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Filtered + sorted users ─────────────────────────────────────────────────
  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    if (q && !u.personalInfo?.name?.toLowerCase().includes(q) &&
             !u.personalInfo?.email?.toLowerCase().includes(q) &&
             !u.personalInfo?.phone?.toLowerCase().includes(q)) return false;
    if (filterStatus !== 'all' && u.membership?.status !== filterStatus) return false;
    if (filterRole   !== 'all' && u.personalInfo?.role?.toLowerCase() !== filterRole) return false;
    if (filterFace === 'set'   && !u.personalInfo?.facePhotoUrl) return false;
    if (filterFace === 'unset' && u.personalInfo?.facePhotoUrl)  return false;
    if (filterBlacklist === 'blacklisted' && !u.blacklisted) return false;
    if (filterBlacklist === 'active'      && u.blacklisted)  return false;
    return true;
  }).sort((a, b) => {
    let av, bv;
    if (sortBy === 'name')    { av = a.personalInfo?.name || ''; bv = b.personalInfo?.name || ''; }
    if (sortBy === 'status')  { av = a.membership?.status || ''; bv = b.membership?.status || ''; }
    if (sortBy === 'expiry')  { av = a.membership?.expiryDate || 0; bv = b.membership?.expiryDate || 0; }
    if (sortBy === 'wallet')  { av = a.wallet?.balance || 0; bv = b.wallet?.balance || 0; }
    if (sortBy === 'joined')  { av = a.personalInfo?.registeredAt || 0; bv = b.personalInfo?.registeredAt || 0; }
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const stats = {
    total:    users.length,
    active:   users.filter(u => u.membership?.status === 'active').length,
    expired:  users.filter(u => u.membership?.status === 'expired').length,
    noFace:   users.filter(u => !u.personalInfo?.facePhotoUrl).length,
    blacklisted: users.filter(u => u.blacklisted).length,
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortTh = ({ col, children }) => (
    <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide cursor-pointer select-none whitespace-nowrap"
      onClick={() => toggleSort(col)}>
      <span className="flex items-center gap-1">
        {children}
        {sortBy === col && <span className="opacity-70">{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );

  const clearFilters = () => {
    setSearch(''); setFilterStatus('all'); setFilterRole('all');
    setFilterFace('all'); setFilterBlacklist('all');
  };

  const hasFilters = search || filterStatus !== 'all' || filterRole !== 'all' ||
                     filterFace !== 'all' || filterBlacklist !== 'all';

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#e9ecef 100%)' }}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8">

        {/* ── HEADER ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
              <span className="px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wide"
                style={{
                  background: isSuperAdmin ? DARK    : '#fef2f2',
                  color:      isSuperAdmin ? '#fff'  : PRIMARY,
                }}>
                {isSuperAdmin ? '⚡ Super Admin' : '🛡 Admin'}
              </span>
            </div>
            <p className="text-gray-500 mt-1 text-sm">
              Manage members, memberships, access and account settings
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchUsers}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 shadow-sm transition-all">
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => exportMembersPDF(filtered)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all"
              style={{ background: `linear-gradient(135deg,${PRIMARY},#b30000)` }}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export PDF
            </button>
          </div>
        </div>

        {/* ── STAT PILLS ── */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total Members',  val: stats.total,       color: DARK    },
            { label: 'Active',         val: stats.active,      color: '#16a34a' },
            { label: 'Expired',        val: stats.expired,     color: '#dc2626' },
            { label: 'No Face Scan',   val: stats.noFace,      color: '#d97706' },
            { label: 'Blacklisted',    val: stats.blacklisted, color: '#6b7280' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 flex items-center gap-3">
              <div>
                <p className="text-xl font-bold" style={{ color: s.color }}>{s.val}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── SEARCH + FILTER BAR ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1" style={{ minWidth: 200 }}>
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email or phone…"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                style={{ '--tw-ring-color': PRIMARY }}
              />
              {search && (
                <button onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            <button onClick={() => setShowFilters(f => !f)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all"
              style={{
                borderColor: showFilters || hasFilters ? PRIMARY : '#e5e7eb',
                color:       showFilters || hasFilters ? PRIMARY : '#6b7280',
                background:  showFilters || hasFilters ? `${PRIMARY}08` : '#fff',
              }}>
              <FunnelIcon className="h-4 w-4" />
              Filters
              {hasFilters && (
                <span className="px-1.5 py-0.5 rounded-full text-xs text-white font-bold"
                  style={{ background: PRIMARY }}>
                  {[filterStatus !== 'all', filterRole !== 'all', filterFace !== 'all', filterBlacklist !== 'all'].filter(Boolean).length}
                </span>
              )}
            </button>

            {hasFilters && (
              <button onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold bg-white border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 transition-all">
                <XMarkIcon className="h-3.5 w-3.5" /> Clear
              </button>
            )}
          </div>

          {/* Expanded filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-100">
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Membership Status</label>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white">
                      <option value="all">All statuses</option>
                      {MEMBERSHIP_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Role</label>
                    <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white">
                      <option value="all">All roles</option>
                      {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Face Scan</label>
                    <select value={filterFace} onChange={e => setFilterFace(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white">
                      <option value="all">All</option>
                      <option value="set">Face scan set</option>
                      <option value="unset">No face scan</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Account Status</label>
                    <select value={filterBlacklist} onChange={e => setFilterBlacklist(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white">
                      <option value="all">All</option>
                      <option value="active">Active accounts</option>
                      <option value="blacklisted">Blacklisted</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── RESULTS COUNT ── */}
        <p className="text-xs text-gray-400 mb-3 font-medium px-1">
          {filtered.length} member{filtered.length !== 1 ? 's' : ''} found
        </p>

        {/* ── TABLE ── */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
            <p className="text-gray-500 mt-3 text-sm">Loading members…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <UserGroupIcon className="h-12 w-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 font-medium text-sm">No members match your filters</p>
            <button onClick={clearFilters} className="mt-3 text-sm font-semibold" style={{ color: PRIMARY }}>
              Clear filters
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide w-10">#</th>
                    <SortTh col="name">Member</SortTh>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide">Role</th>
                    <SortTh col="status">Membership</SortTh>
                    <SortTh col="expiry">Expiry</SortTh>
                    <SortTh col="wallet">Wallet</SortTh>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide">Face</th>
                    <SortTh col="joined">Joined</SortTh>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-white/80 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user, idx) => (
                    <UserRow
                      key={user.id}
                      user={user}
                      idx={idx}
                      isAuthorized={isAuthorized}
                      onView={() => setViewModal(user)}
                      onEdit={() => setEditModal(user)}
                      onRefresh={fetchUsers}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── VIEW MODAL ── */}
      <AnimatePresence>
        {viewModal && (
          <ViewUserModal
            user={viewModal}
            onClose={() => setViewModal(null)}
            onEdit={() => { setEditModal(viewModal); setViewModal(null); }}
          />
        )}
      </AnimatePresence>

      {/* ── EDIT MODAL ── */}
      <AnimatePresence>
        {editModal && (
          <EditUserModal
            user={editModal}
            isAuthorized={isAuthorized}
            isSuperAdmin={isSuperAdmin}
            currentUserId={currentUser?.uid}
            onClose={() => setEditModal(null)}
            onSaved={() => { setEditModal(null); fetchUsers(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// USER ROW
// ═══════════════════════════════════════════════════════════════════════════════
const UserRow = ({ user, idx, isAuthorized, onView, onEdit, onRefresh }) => {
  const mb   = getMembershipBadge(user.membership?.status, user.membership?.expiryDate);
  const role = getRoleBadge(user.personalInfo?.role?.toLowerCase());
  const days = daysLeft(user.membership?.expiryDate);

  const handleBlacklist = async (e) => {
    e.stopPropagation();
    const isBlacklisted = !!user.blacklisted;
    const result = await Swal.fire({
      title: isBlacklisted ? 'Remove Blacklist?' : 'Blacklist Member?',
      text: isBlacklisted
        ? `Restore ${user.personalInfo?.name}'s access?`
        : `This will prevent ${user.personalInfo?.name} from accessing the gym.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: isBlacklisted ? '#16a34a' : '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: isBlacklisted ? 'Yes, restore' : 'Yes, blacklist',
      customClass: { container: 'um-swal-z' },
    });
    if (!result.isConfirmed) return;
    try {
      await update(ref(database, `users/${user.id}`), {
        blacklisted: !isBlacklisted,
        blacklistedAt: !isBlacklisted ? Date.now() : null,
      });
      toast('success', isBlacklisted ? 'Access restored' : 'Member blacklisted');
      onRefresh();
    } catch (err) {
      toast('error', 'Failed to update');
    }
  };

  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: idx * 0.015 }}
      className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors"
      style={{ background: user.blacklisted ? '#fef2f2' : undefined }}>

      <td className="px-3 py-3 text-xs text-gray-400 font-mono">{idx + 1}</td>

      {/* Member */}
      <td className="px-3 py-3">
        <div className="flex items-center gap-3">
          {user.personalInfo?.facePhotoUrl ? (
            <img src={user.personalInfo.facePhotoUrl} alt=""
              className="w-9 h-9 rounded-full object-cover flex-shrink-0 border-2"
              style={{ borderColor: mb.color + '40' }} />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
              style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
              {(user.personalInfo?.name || 'U')[0].toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="font-semibold text-gray-900 text-sm truncate max-w-[130px]">{user.personalInfo?.name || '—'}</p>
              {user.blacklisted && (
                <span className="text-xs px-1.5 py-0.5 rounded-full font-bold bg-red-100 text-red-600 flex-shrink-0">Blacklisted</span>
              )}
            </div>
            <p className="text-xs text-gray-400 truncate max-w-[130px]">{user.personalInfo?.email || '—'}</p>
          </div>
        </div>
      </td>

      {/* Role */}
      <td className="px-3 py-3">
        <span className="text-xs font-bold px-2.5 py-1 rounded-full"
          style={{ background: role.bg, color: role.color }}>
          {role.label}
        </span>
      </td>

      {/* Membership */}
      <td className="px-3 py-3">
        <span className="text-xs font-bold px-2.5 py-1 rounded-full"
          style={{ background: mb.bg, color: mb.color }}>
          {mb.label}
        </span>
      </td>

      {/* Expiry */}
      <td className="px-3 py-3">
        <p className="text-xs text-gray-600">{fmtDate(user.membership?.expiryDate)}</p>
        {days !== null && days > 0 && days <= 30 && user.membership?.status === 'active' && (
          <p className="text-xs font-semibold mt-0.5" style={{ color: days <= 7 ? '#dc2626' : '#d97706' }}>
            {days}d left
          </p>
        )}
      </td>

      {/* Wallet */}
      <td className="px-3 py-3">
        <span className="text-xs font-bold text-gray-800">
          KES {(user.wallet?.balance || 0).toLocaleString()}
        </span>
      </td>

      {/* Face */}
      <td className="px-3 py-3">
        {user.personalInfo?.facePhotoUrl ? (
          <span className="text-xs text-green-600 flex items-center gap-1">
            <CheckCircleIcon className="h-3.5 w-3.5" /> Set
          </span>
        ) : (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <XMarkIcon className="h-3.5 w-3.5" /> Not set
          </span>
        )}
      </td>

      {/* Joined */}
      <td className="px-3 py-3">
        <p className="text-xs text-gray-500">{fmtDate(user.personalInfo?.registeredAt)}</p>
      </td>

      {/* Actions */}
      <td className="px-3 py-3">
        <div className="flex items-center justify-center gap-1.5">
          <button onClick={onView}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
            style={{ background: '#eff6ff', color: '#2563eb' }}
            onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
            onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}>
            <EyeIcon className="h-3.5 w-3.5" /> View
          </button>
          {isAuthorized && (
            <>
              <button onClick={onEdit}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                style={{ background: '#fef2f2', color: PRIMARY }}
                onMouseEnter={e => e.currentTarget.style.background = '#fcd5de'}
                onMouseLeave={e => e.currentTarget.style.background = '#fef2f2'}>
                <PencilIcon className="h-3.5 w-3.5" /> Edit
              </button>
              <button onClick={handleBlacklist}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                style={{ background: user.blacklisted ? '#f0fdf4' : '#fef2f2', color: user.blacklisted ? '#16a34a' : '#dc2626' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <NoSymbolIcon className="h-3.5 w-3.5" />
                {user.blacklisted ? 'Restore' : 'Block'}
              </button>
            </>
          )}
        </div>
      </td>
    </motion.tr>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// VIEW USER MODAL — Full screen read-only with guest visits
// ═══════════════════════════════════════════════════════════════════════════════
const ViewUserModal = ({ user, onClose, onEdit }) => {
  const [activeTab,    setActiveTab]    = useState('profile');
  const [guestVisits,  setGuestVisits]  = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [paymentUIDs,  setPaymentUIDs]  = useState([]);

  const mb   = getMembershipBadge(user.membership?.status, user.membership?.expiryDate);
  const role = getRoleBadge(user.personalInfo?.role?.toLowerCase());

  useEffect(() => {
    if (activeTab === 'visits') {
      setVisitsLoading(true);
      get(ref(database, `users/${user.id}/guestVisits`)).then(snap => {
        if (snap.exists()) {
          const list = Object.entries(snap.val())
            .map(([k, v]) => ({ id: k, ...v }))
            .sort((a, b) => b.checkedIn - a.checkedIn);
          setGuestVisits(list);
        } else {
          setGuestVisits([]);
        }
      }).catch(console.error).finally(() => setVisitsLoading(false));
    }
    if (activeTab === 'payments') {
      get(ref(database, `users/${user.id}/paymentUIDs`)).then(snap => {
        if (snap.exists()) {
          const list = Object.entries(snap.val())
            .map(([k, v]) => ({ id: k, ...v }))
            .sort((a, b) => (b.activationDate || b.createdAt || 0) - (a.activationDate || a.createdAt || 0));
          setPaymentUIDs(list);
        }
      }).catch(console.error);
    }
  }, [activeTab, user.id]);

  const TABS = [
    { key: 'profile',  label: 'Profile',       icon: <UserIcon        className="h-4 w-4" /> },
    { key: 'membership', label: 'Membership',  icon: <SparklesIcon    className="h-4 w-4" /> },
    { key: 'wallet',   label: 'Wallet',         icon: <WalletIcon      className="h-4 w-4" /> },
    { key: 'visits',   label: 'Guest Visits',   icon: <ClockIcon       className="h-4 w-4" /> },
    { key: 'payments', label: 'Payments',       icon: <CreditCardIcon  className="h-4 w-4" /> },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
          <div className="flex items-center gap-4">
            {user.personalInfo?.facePhotoUrl ? (
              <img src={user.personalInfo.facePhotoUrl} alt=""
                className="w-12 h-12 rounded-full object-cover border-2 border-white/30 flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-xl flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.2)' }}>
                {(user.personalInfo?.name || 'U')[0].toUpperCase()}
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-white">{user.personalInfo?.name || 'Unknown'}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                  style={{ background: role.bg, color: role.color }}>{role.label}</span>
                <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                  style={{ background: mb.bg, color: mb.color }}>{mb.label}</span>
                {user.blacklisted && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-red-100 text-red-600">🚫 Blacklisted</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onEdit}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all"
              style={{ background: 'rgba(255,255,255,0.2)' }}>
              <PencilIcon className="h-4 w-4" /> Edit
            </button>
            <button onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-xl text-white"
              style={{ background: 'rgba(255,255,255,0.15)' }}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex gap-1 px-8 py-3 border-b border-gray-100 bg-gray-50/50 overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all"
              style={{
                background: activeTab === tab.key ? PRIMARY : 'transparent',
                color:      activeTab === tab.key ? '#fff'  : '#6b7280',
              }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6" style={{ minHeight: 0 }}>
          <div className="max-w-4xl mx-auto">

            {/* ── PROFILE TAB ── */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Full Name',     val: user.personalInfo?.name,          icon: <UserIcon         className="h-4 w-4" /> },
                    { label: 'Email',         val: user.personalInfo?.email,          icon: <EnvelopeIcon     className="h-4 w-4" /> },
                    { label: 'Phone',         val: user.personalInfo?.phone,          icon: <PhoneIcon        className="h-4 w-4" /> },
                    { label: 'Role',          val: user.personalInfo?.role,           icon: <ShieldCheckIcon  className="h-4 w-4" /> },
                    { label: 'Joined',        val: fmtDateTime(user.personalInfo?.registeredAt), icon: <CalendarIcon className="h-4 w-4" /> },
                    { label: 'Last Login',    val: fmtDateTime(user.personalInfo?.lastLogin),    icon: <ClockIcon    className="h-4 w-4" /> },
                    { label: 'RFID Card',     val: user.personalInfo?.rfidCard || 'Not assigned', icon: <QrCodeIcon  className="h-4 w-4" /> },
                    { label: 'Provider',      val: user.personalInfo?.provider,       icon: <KeyIcon          className="h-4 w-4" /> },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${PRIMARY}10`, color: PRIMARY }}>
                        {item.icon}
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                        <p className="font-semibold text-gray-900 mt-0.5 text-sm">{item.val || '—'}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Face scan status */}
                <div className="flex items-center gap-4 p-4 rounded-2xl border"
                  style={{
                    background: user.personalInfo?.facePhotoUrl ? '#f0fdf4' : '#fef2f2',
                    borderColor: user.personalInfo?.facePhotoUrl ? '#bbf7d0' : '#fcd5de',
                  }}>
                  {user.personalInfo?.facePhotoUrl ? (
                    <>
                      <img src={user.personalInfo.facePhotoUrl} alt="Face"
                        className="w-16 h-16 rounded-full object-cover border-4 border-green-200 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-green-800 flex items-center gap-2">
                          <CheckCircleIcon className="h-5 w-5 text-green-600" /> Face scan active
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Set on {fmtDate(user.personalInfo?.faceSetupAt)}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <CameraIcon className="h-7 w-7 text-red-400" />
                      </div>
                      <div>
                        <p className="font-bold text-red-800">Face scan not set up</p>
                        <p className="text-xs text-red-600 mt-1">Member cannot use face recognition for gym access</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Blacklist status */}
                {user.blacklisted && (
                  <div className="p-4 rounded-2xl bg-red-50 border border-red-200">
                    <div className="flex items-center gap-3">
                      <NoSymbolIcon className="h-6 w-6 text-red-500 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-red-800">Account Blacklisted</p>
                        <p className="text-xs text-red-600 mt-0.5">
                          Blacklisted on {fmtDate(user.blacklistedAt)} · Member cannot access the gym
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── MEMBERSHIP TAB ── */}
            {activeTab === 'membership' && (
              <div className="space-y-4">
                <div className="p-6 rounded-2xl border"
                  style={{ background: `${mb.color}08`, borderColor: `${mb.color}30` }}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Current Status</p>
                      <span className="text-2xl font-black mt-1 block capitalize" style={{ color: mb.color }}>
                        {user.membership?.status || 'Inactive'}
                      </span>
                    </div>
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                      style={{ background: `${mb.color}15` }}>
                      <SparklesIcon className="h-8 w-8" style={{ color: mb.color }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { label: 'Package',   val: user.membership?.packageName || user.currentPackage?.packageName || '—' },
                      { label: 'Plan',      val: user.membership?.planKey     || '—' },
                      { label: 'Duration',  val: user.membership?.duration ? `${user.membership.duration} days` : '—' },
                      { label: 'Start Date', val: fmtDate(user.membership?.startDate) },
                      { label: 'Expiry',    val: fmtDate(user.membership?.expiryDate) },
                      { label: 'Days Left', val: daysLeft(user.membership?.expiryDate) !== null ? `${Math.max(0, daysLeft(user.membership.expiryDate))} days` : '—' },
                    ].map((item, i) => (
                      <div key={i} className="bg-white rounded-xl p-3 border border-gray-100">
                        <p className="text-xs text-gray-400">{item.label}</p>
                        <p className="font-bold text-gray-900 mt-0.5 text-sm">{item.val}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── WALLET TAB ── */}
            {activeTab === 'wallet' && (
              <div className="space-y-4">
                <div className="p-6 rounded-2xl border"
                  style={{ background: `${PRIMARY}05`, borderColor: `${PRIMARY}20` }}>
                  <p className="text-xs text-gray-500 mb-1">Current Balance</p>
                  <p className="text-3xl font-black" style={{ color: PRIMARY }}>
                    KES {(user.wallet?.balance || 0).toLocaleString()}
                  </p>
                </div>

                {/* Wallet transactions */}
                {user.wallet?.transactions && (
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                      <p className="text-sm font-bold text-gray-700">Transaction History</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            {['Date', 'Type', 'Amount', 'Balance', 'Description'].map(h => (
                              <th key={h} className="px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {Object.entries(user.wallet.transactions)
                            .map(([k, v]) => ({ id: k, ...v }))
                            .sort((a, b) => b.date - a.date)
                            .slice(0, 20)
                            .map(txn => (
                              <tr key={txn.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-xs text-gray-500">{fmtDate(txn.date)}</td>
                                <td className="px-4 py-2">
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${txn.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                    {txn.type}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  <span className={`text-sm font-bold ${txn.type === 'credit' ? 'text-green-700' : 'text-red-600'}`}>
                                    {txn.type === 'credit' ? '+' : '-'}KES {(txn.amount || 0).toLocaleString()}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700 font-medium">KES {(txn.balance || 0).toLocaleString()}</td>
                                <td className="px-4 py-2 text-xs text-gray-500">{txn.description || txn.source || '—'}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── GUEST VISITS TAB ── */}
            {activeTab === 'visits' && (
              <div>
                {visitsLoading ? (
                  <div className="text-center py-10">
                    <div className="w-6 h-6 border-2 border-red-200 border-t-red-700 rounded-full animate-spin mx-auto" />
                  </div>
                ) : guestVisits.length === 0 ? (
                  <div className="text-center py-10 bg-gray-50 rounded-2xl">
                    <ClockIcon className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No guest visits recorded</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
                            {['Activity', 'Schedules', 'Check In', 'Duration', 'Amount', 'Method', 'Status'].map(h => (
                              <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {guestVisits.map(visit => (
                            <tr key={visit.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3">
                                <p className="font-semibold text-gray-900 text-sm">{visit.activityTitle || '—'}</p>
                                {visit.activities?.length > 1 && (
                                  <p className="text-xs text-gray-400">{visit.activities.length} activities</p>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1">
                                  {(visit.scheduleLabels || []).slice(0, 2).map((s, i) => (
                                    <span key={i} className="text-xs px-1.5 py-0.5 rounded"
                                      style={{ background: `${PRIMARY}10`, color: PRIMARY }}>{s}</span>
                                  ))}
                                  {(visit.scheduleLabels || []).length > 2 && (
                                    <span className="text-xs text-gray-400">+{visit.scheduleLabels.length - 2}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{fmtDateTime(visit.checkedIn)}</td>
                              <td className="px-4 py-3 text-xs text-gray-600">{fmtDuration(visit.actualDurationMinutes)}</td>
                              <td className="px-4 py-3">
                                <span className="font-bold text-sm text-gray-900">KES {(visit.amountPaid || visit.totalPrice || 0).toLocaleString()}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                                  style={{ background: visit.paymentMethod === 'cash' ? '#f0fdf4' : visit.paymentMethod === 'wallet' ? '#fef2f2' : '#eff6ff', color: visit.paymentMethod === 'cash' ? '#16a34a' : visit.paymentMethod === 'wallet' ? PRIMARY : '#2563eb' }}>
                                  {visit.paymentMethod}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${visit.status === 'completed' ? 'bg-green-100 text-green-700' : visit.status === 'ongoing' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {visit.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── PAYMENTS TAB ── */}
            {activeTab === 'payments' && (
              <div className="space-y-4">
                {paymentUIDs.length === 0 ? (
                  <div className="text-center py-10 bg-gray-50 rounded-2xl">
                    <CreditCardIcon className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No payment records found</p>
                  </div>
                ) : paymentUIDs.map(uid => (
                  <div key={uid.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100"
                      style={{ background: uid.status === 'completed' ? '#f0fdf4' : '#fef2f2' }}>
                      <div>
                        <p className="font-bold text-gray-900">{uid.packageName || uid.packageId} — {uid.planKey}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Activated: {fmtDate(uid.activationDate)} · Expires: {fmtDate(uid.expiryDate)}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${uid.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {uid.status}
                        </span>
                        <p className="font-black text-lg mt-1" style={{ color: PRIMARY }}>
                          KES {(uid.packagePrice || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {uid.payments && (
                      <div className="px-5 py-3">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Payment Indexes</p>
                        <div className="space-y-1.5">
                          {Object.entries(uid.payments).map(([k, pay]) => (
                            <div key={k} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                              <div className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                                  style={{ background: PRIMARY }}>{pay.index ?? k}</span>
                                <span className="text-gray-600 text-xs">{pay.method}</span>
                                {pay.reference && <span className="text-gray-400 text-xs font-mono">{pay.reference}</span>}
                              </div>
                              <div className="text-right">
                                <span className="font-bold text-gray-900">KES {(pay.amount || 0).toLocaleString()}</span>
                                <span className="text-xs text-gray-400 ml-2">{fmtDate(pay.date)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {uid.overpayment > 0 && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-green-600 font-medium">
                            <CheckCircleIcon className="h-3.5 w-3.5" />
                            KES {uid.overpayment.toLocaleString()} overpayment → credited to wallet
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT USER MODAL — Full screen edit everything
// ═══════════════════════════════════════════════════════════════════════════════
const EditUserModal = ({ user, isAuthorized, isSuperAdmin, currentUserId, onClose, onSaved }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const [saving,    setSaving]    = useState(false);

  // Personal info
  const [name,   setName]   = useState(user.personalInfo?.name  || '');
  const [phone,  setPhone]  = useState(user.personalInfo?.phone || '');
  const [rfid,   setRfid]   = useState(user.personalInfo?.rfidCard || '');
  const [role,   setRole]   = useState(user.personalInfo?.role?.toLowerCase() || 'user');

  // Membership
  const [memStatus,  setMemStatus]  = useState(user.membership?.status     || 'inactive');
  const [memPackage, setMemPackage] = useState(user.membership?.packageName || user.currentPackage?.packageName || '');
  const [memPlan,    setMemPlan]    = useState(user.membership?.planKey     || 'monthly');
  const [memStart,   setMemStart]   = useState(
    user.membership?.startDate ? new Date(user.membership.startDate).toISOString().slice(0, 10) : ''
  );
  const [memExpiry, setMemExpiry]   = useState(
    user.membership?.expiryDate ? new Date(user.membership.expiryDate).toISOString().slice(0, 10) : ''
  );

  // Wallet
  const [walletBalance, setWalletBalance] = useState(user.wallet?.balance || 0);
  const [walletNote,    setWalletNote]    = useState('');

  // Auto-calculate expiry from start + plan
  const autoExpiry = () => {
    if (!memStart) return;
    const plan = PLAN_KEYS.find(p => p.key === memPlan);
    if (!plan) return;
    const expiry = new Date(memStart);
    expiry.setDate(expiry.getDate() + plan.days);
    setMemExpiry(expiry.toISOString().slice(0, 10));
  };

  const handleSave = async () => {
    if (!isAuthorized) return toast('error', 'Access Denied');
    setSaving(true);
    try {
      const now = Date.now();
      const updates = {};

      // Personal info
      updates[`users/${user.id}/personalInfo/name`]    = name.trim();
      updates[`users/${user.id}/personalInfo/phone`]   = phone.trim();
      updates[`users/${user.id}/personalInfo/rfidCard`] = rfid.trim();
      if (isSuperAdmin) {
        updates[`users/${user.id}/personalInfo/role`] = role;
      }

      // Membership
      updates[`users/${user.id}/membership/status`]      = memStatus;
      updates[`users/${user.id}/membership/packageName`] = memPackage;
      updates[`users/${user.id}/membership/planKey`]     = memPlan;
      if (memStart)  updates[`users/${user.id}/membership/startDate`]  = new Date(memStart).getTime();
      if (memExpiry) updates[`users/${user.id}/membership/expiryDate`] = new Date(memExpiry).getTime();

      const plan = PLAN_KEYS.find(p => p.key === memPlan);
      if (plan) updates[`users/${user.id}/membership/duration`] = plan.days;

      // Wallet balance (only if changed)
      if (walletBalance !== (user.wallet?.balance || 0)) {
        const diff = walletBalance - (user.wallet?.balance || 0);
        updates[`users/${user.id}/wallet/balance`] = walletBalance;
        // Record admin adjustment transaction
        const txnKey = `txn_admin_${now}`;
        updates[`users/${user.id}/wallet/transactions/${txnKey}`] = {
          date:        now,
          type:        diff > 0 ? 'credit' : 'debit',
          amount:      Math.abs(diff),
          source:      'admin_adjustment',
          description: walletNote || 'Admin wallet adjustment',
          balance:     walletBalance,
        };
      }

      await update(ref(database, '/'), updates);
      toast('success', 'Member updated successfully');
      onSaved();
    } catch (err) {
      console.error(err);
      toast('error', 'Failed to save', err.message);
    } finally {
      setSaving(false);
    }
  };

  const EDIT_TABS = [
    { key: 'personal',   label: 'Personal',   icon: <UserIcon       className="h-4 w-4" /> },
    { key: 'membership', label: 'Membership', icon: <SparklesIcon   className="h-4 w-4" /> },
    { key: 'wallet',     label: 'Wallet',     icon: <WalletIcon     className="h-4 w-4" /> },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
          <div>
            <h2 className="text-xl font-bold text-white">Edit Member — {user.personalInfo?.name}</h2>
            <p className="text-white/60 text-sm mt-0.5">Full control over member account and membership</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white"
            style={{ background: 'rgba(255,255,255,0.15)' }}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex gap-1 px-8 py-3 border-b border-gray-100 bg-gray-50/50">
          {EDIT_TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
              style={{
                background: activeTab === tab.key ? PRIMARY : 'transparent',
                color:      activeTab === tab.key ? '#fff'  : '#6b7280',
              }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6" style={{ minHeight: 0 }}>
          <div className="max-w-3xl mx-auto space-y-6">

            {/* ── PERSONAL TAB ── */}
            {activeTab === 'personal' && (
              <div className="space-y-5">
                <EditSection title="Basic Information">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <EditField label="Full Name *" value={name} onChange={setName} placeholder="John Kamau" />
                    <EditField label="Phone Number" value={phone} onChange={setPhone} placeholder="+254 712 345 678" type="tel" />
                    <EditField label="RFID Card Number" value={rfid} onChange={setRfid} placeholder="e.g., RFID123456" />
                    {isSuperAdmin && (
                      <div>
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">
                          Role
                        </label>
                        <select value={role} onChange={e => setRole(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                          style={{ '--tw-ring-color': PRIMARY }}>
                          {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                        </select>
                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                          <ExclamationTriangleIcon className="h-3 w-3" /> Changing role affects dashboard access
                        </p>
                      </div>
                    )}
                  </div>
                </EditSection>

                {/* Read-only email */}
                <EditSection title="Account Info (Read Only)">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <p className="text-xs text-gray-400">Email (cannot change)</p>
                      <p className="font-semibold text-gray-700 mt-0.5">{user.personalInfo?.email || '—'}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <p className="text-xs text-gray-400">Registered</p>
                      <p className="font-semibold text-gray-700 mt-0.5">{fmtDate(user.personalInfo?.registeredAt)}</p>
                    </div>
                  </div>
                </EditSection>
              </div>
            )}

            {/* ── MEMBERSHIP TAB ── */}
            {activeTab === 'membership' && (
              <div className="space-y-5">
                <EditSection title="Membership Status">
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {MEMBERSHIP_STATUSES.map(s => (
                      <button key={s} type="button" onClick={() => setMemStatus(s)}
                        className="flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all"
                        style={{
                          borderColor: memStatus === s ? getMembershipBadge(s).color : '#e5e7eb',
                          background:  memStatus === s ? getMembershipBadge(s).bg    : '#fff',
                        }}>
                        <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                          style={{
                            borderColor: memStatus === s ? getMembershipBadge(s).color : '#d1d5db',
                            background:  memStatus === s ? getMembershipBadge(s).color : 'transparent',
                          }}>
                          {memStatus === s && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <span className="text-sm font-semibold capitalize text-gray-800">{s}</span>
                      </button>
                    ))}
                  </div>
                </EditSection>

                <EditSection title="Package & Plan">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <EditField label="Package Name" value={memPackage} onChange={setMemPackage} placeholder="e.g., Monthly Membership" />
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Plan</label>
                      <select value={memPlan} onChange={e => setMemPlan(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                        style={{ '--tw-ring-color': PRIMARY }}>
                        {PLAN_KEYS.map(p => (
                          <option key={p.key} value={p.key}>{p.label} ({p.days} days)</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </EditSection>

                <EditSection title="Dates">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <EditField label="Start Date" value={memStart} onChange={setMemStart} type="date" />
                    <div>
                      <EditField label="Expiry Date" value={memExpiry} onChange={setMemExpiry} type="date" />
                      <button onClick={autoExpiry} type="button"
                        className="mt-2 text-xs font-semibold flex items-center gap-1.5 transition-all"
                        style={{ color: PRIMARY }}>
                        <BoltIcon className="h-3.5 w-3.5" />
                        Auto-calculate from start + plan ({PLAN_KEYS.find(p => p.key === memPlan)?.days} days)
                      </button>
                    </div>
                  </div>
                </EditSection>

                {/* Current membership preview */}
                {memStart && memExpiry && (
                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Preview</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-600">{memPackage || 'Package'}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-600">{PLAN_KEYS.find(p => p.key === memPlan)?.label}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-600">{fmtDate(new Date(memStart).getTime())}</span>
                      <span className="text-gray-400">→</span>
                      <span className="font-bold" style={{ color: PRIMARY }}>{fmtDate(new Date(memExpiry).getTime())}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── WALLET TAB ── */}
            {activeTab === 'wallet' && (
              <div className="space-y-5">
                <EditSection title="Wallet Balance">
                  <div className="p-4 rounded-2xl mb-4"
                    style={{ background: `${PRIMARY}05`, border: `1px solid ${PRIMARY}20` }}>
                    <p className="text-xs text-gray-500 mb-1">Current Balance</p>
                    <p className="text-2xl font-black" style={{ color: PRIMARY }}>
                      KES {(user.wallet?.balance || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">
                        New Balance (KES)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">KES</span>
                        <input
                          type="number" min="0" value={walletBalance}
                          onChange={e => setWalletBalance(parseFloat(e.target.value) || 0)}
                          className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                          style={{ '--tw-ring-color': PRIMARY }}
                        />
                      </div>
                      {walletBalance !== (user.wallet?.balance || 0) && (
                        <p className="text-xs mt-1.5 font-semibold"
                          style={{ color: walletBalance > (user.wallet?.balance || 0) ? '#16a34a' : '#dc2626' }}>
                          {walletBalance > (user.wallet?.balance || 0) ? '+' : ''}
                          KES {(walletBalance - (user.wallet?.balance || 0)).toLocaleString()} change
                        </p>
                      )}
                    </div>

                    <EditField
                      label="Adjustment Note (optional)"
                      value={walletNote}
                      onChange={setWalletNote}
                      placeholder="e.g., Cash top-up at reception"
                    />

                    <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                      <p className="text-xs text-amber-700 flex items-start gap-2">
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        Changing wallet balance creates an admin adjustment transaction record for audit purposes.
                      </p>
                    </div>
                  </div>
                </EditSection>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
          <button onClick={onClose}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-10 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg transition-all disabled:opacity-50 flex items-center gap-2"
            style={{ background: `linear-gradient(135deg,${PRIMARY},#b30000)` }}>
            {saving
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
              : <><CheckIcon className="h-4 w-4" /> Save Changes</>
            }
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── SHARED EDIT COMPONENTS ───────────────────────────────────────────────────
const EditSection = ({ title, children }) => (
  <div>
    <div className="flex items-center gap-3 mb-4">
      <p className="text-base font-bold text-gray-900 whitespace-nowrap">{title}</p>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
    {children}
  </div>
);

const EditField = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
      style={{ '--tw-ring-color': PRIMARY }}
    />
  </div>
);

export default UserManagement;